/**
 * Turning the state machine's names into words a person reads (PLAN §13.1).
 *
 * `queued`, `waiting` and `needs_action` are engineering terms. The owner sees
 * "Queued", "Waiting for TikTok" and "Needs you" instead — and every one is
 * paired with a tone that the badge renders as a glyph, so status is never
 * carried by colour alone.
 */
import type { ConnectionStatus, PostStatus, TargetStatus } from '@dintiko/shared';
import type { StatusTone } from '../components/status.js';

interface Label {
  tone: StatusTone;
  label: string;
}

const targetLabels: Record<TargetStatus, Label> = {
  draft: { tone: 'idle', label: 'Draft' },
  scheduled: { tone: 'idle', label: 'Scheduled' },
  queued: { tone: 'busy', label: 'Queued' },
  running: { tone: 'busy', label: 'Uploading' },
  waiting: { tone: 'busy', label: 'Processing' },
  published: { tone: 'ok', label: 'Published' },
  needs_action: { tone: 'warn', label: 'Needs you' },
  failed: { tone: 'bad', label: 'Failed' },
  cancelled: { tone: 'idle', label: 'Cancelled' },
};

export function targetStatusLabel(status: TargetStatus, platform?: string | null): Label {
  const base = targetLabels[status];
  // "Processing" alone is ambiguous when six accounts are in flight; naming the
  // platform makes a row readable on its own.
  if (status === 'waiting' && platform) {
    return { tone: base.tone, label: `Waiting for ${platformName(platform)}` };
  }
  return base;
}

const postLabels: Record<PostStatus, Label> = {
  draft: { tone: 'idle', label: 'Draft' },
  scheduled: { tone: 'idle', label: 'Scheduled' },
  publishing: { tone: 'busy', label: 'Publishing' },
  published: { tone: 'ok', label: 'Published' },
  partial: { tone: 'warn', label: 'Partly published' },
  failed: { tone: 'bad', label: 'Failed' },
  cancelled: { tone: 'idle', label: 'Cancelled' },
};

export function postStatusLabel(status: PostStatus): Label {
  return postLabels[status];
}

export function connectionStatusLabel(status: ConnectionStatus): Label {
  switch (status) {
    case 'active':
      return { tone: 'ok', label: 'Connected' };
    case 'expiring':
      return { tone: 'warn', label: 'Expiring' };
    case 'needs_reconnect':
      return { tone: 'bad', label: 'Reconnect needed' };
    // Grey, not red. The owner did this on purpose; the only reason the card
    // is still here is that its posts are.
    case 'revoked':
      return { tone: 'idle', label: 'Disconnected' };
  }
}

/**
 * Brand names, spelled the way the platform spells itself.
 *
 * This is the third copy of this table in the app (components/chip.tsx and
 * components/platform-logo.tsx carry the others), and being third is how it
 * came to be the one missing Telegram — `platformName('telegram')` fell
 * through to the `?? platform` fallback and rendered the lowercase slug in the
 * Connect list and in "Waiting for telegram". Adding a platform should not
 * require remembering three files; see the note on PLATFORM_LABELS in
 * components/platform-logo.tsx.
 */
const platformNames: Record<string, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  telegram: 'Telegram',
  mock: 'the mock platform',
};

export function platformName(platform: string): string {
  return platformNames[platform] ?? platform;
}

/** Statuses the owner is expected to act on, for the dashboard's first section. */
export function needsAttention(status: TargetStatus): boolean {
  return status === 'failed' || status === 'needs_action';
}
